import { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Controller, useForm } from "react-hook-form";

import useAuthHook from "../../hooks/auth.hook";

import {
  Rating,
  Stack,
  Typography,
  Grid,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { createReview } from "../../redux/actions/reviews.action";
import {
  changeAppStatus,
  changeModalStatus,
} from "../../redux/actions/general.action";

import CustomInput from "../Common/CustomInput.component";
import CustomButton from "./CustomButton.component";
import CustomPagination from "./CustomPagination.component";

import {
  calculateTotalPageByTotalDocs,
  convertISODateStringToDate,
  getUsernameFromEmail,
} from "../../services/utils";
import { DOCS_PER_PAGE } from "../../services/constants";

const reviewLabels = {
  0.5: "Dở ẹc luôn",
  1: "Siêu dở",
  1.5: "Dở",
  2: "Không hay",
  2.5: "Bình thường",
  3: "Nên đọc",
  3.5: "Khá hay",
  4: "Hay",
  4.5: "Cực hay",
  5: "Phải đọc",
};

const Reviews = (props) => {
  const [mounted, setMounted] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingValueHover, setRatingValueHover] = useState(-1);
  const [checkRequireRating, setCheckRequireRating] = useState(false);
  const [currentReviewPage, setCurrentReviewPage] = useState(1);
  const [skipReview, setSkipReview] = useState(0);
  const { currentUser } = useAuthHook();
  const { appStatus } = useSelector((state) => state.general);

  const dispatch = useDispatch();
  const theme = useTheme();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    shouldUnregister: true,
  });

  const handleChangeRatingValue = (event, value) => {
    setRatingValue(value);
    setCheckRequireRating(false);
  };

  const handleChangeRatingValueHover = (event, value) => {
    setTimeout(() => {
      setRatingValueHover(value);
    }, 500);
  };

  const handleSignInToReview = () => {
    dispatch(changeModalStatus("open"));
  };

  const handleSubmitReview = (data) => {
    if (ratingValue === 0) {
      setCheckRequireRating(true);
      return;
    }
    dispatch(changeAppStatus("loading"));
    const reviewData = {
      content: data.review,
      rating: ratingValue,
      book: props?.book?._id,
      reviewer: currentUser._id,
    };
    dispatch(createReview(reviewData, props.token));
    setRatingValue(0);
    reset({});
  };

  const reviewInfoOfCurrentUser = useMemo(() => {
    if (currentUser) {
      const result = props.book.reviews.find(
        (review) => review.reviewer._id === currentUser._id
      );
      return result;
    }
  }, [currentUser, props]);

  const totalReviews = useMemo(() => {
    return calculateTotalPageByTotalDocs(props?.book?.reviews?.length);
  }, [props]);

  const handleChangePagination = (event, page) => {
    setCurrentReviewPage(page);
    setSkipReview((page - 1) * DOCS_PER_PAGE);
  };

  useEffect(() => {
    setMounted(true);
    return () => {
      setMounted(false);
    };
  }, [currentUser]);

  return (
    mounted && (
      <Grid container spacing={10}>
        <Grid item xs={12} sm={12} md={5}>
          <Stack alignItems="center" mb={5}>
            <Typography variant="h6">Đánh giá trung bình:</Typography>
            <Stack alignItems="center">
              <Typography variant="h1">
                {props?.book?.ratingAverage?.toFixed(1)}
              </Typography>
              <Rating
                value={props.book.ratingAverage || 0}
                precision={0.5}
                size="large"
                readOnly
              />
              <Typography variant="body1">
                {`(${
                  reviewLabels[Math.round(props.book.ratingAverage)] ||
                  "Chưa đánh giá"
                })`}
              </Typography>
            </Stack>
          </Stack>
          <Divider light />
          {!currentUser ? (
            <Stack spacing={4} alignItems="center" mt={2}>
              <Typography>Vui lòng đăng nhập để đánh giá sách này!</Typography>
              <CustomButton variant="contained" onClick={handleSignInToReview}>
                Đăng nhập ngay
              </CustomButton>
            </Stack>
          ) : reviewInfoOfCurrentUser ? (
            <Stack spacing={5} alignItems="center">
              <Typography variant="h6" mt={3} mb={1}>
                Đánh giá & nhận xét của bạn:
              </Typography>
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center">
                  <Rating
                    value={reviewInfoOfCurrentUser.rating || 0}
                    precision={0.5}
                    readOnly
                  />
                  <Typography variant="caption" mt={1}>{`(${
                    reviewLabels[reviewInfoOfCurrentUser.rating]
                  })`}</Typography>
                </Stack>
                <Typography style={{ whiteSpace: "pre-line" }}>
                  {" "}
                  {reviewInfoOfCurrentUser.content}
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <form onSubmit={handleSubmit(handleSubmitReview)} noValidate>
              <Stack alignItems="center" spacing={3}>
                <Typography variant="h6" mt={3} mb={1}>
                  Đánh giá & nhận xét của bạn:
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "column", md: "row" }}
                  alignItems="center"
                >
                  <Rating
                    value={ratingValue || 0}
                    precision={0.5}
                    onChange={handleChangeRatingValue}
                    onChangeActive={handleChangeRatingValueHover}
                    size="medium"
                  />
                  <Typography variant="caption" mt={1}>{`(${
                    ratingValue !== 0
                      ? reviewLabels[
                          ratingValueHover !== -1
                            ? ratingValueHover
                            : ratingValue
                        ]
                      : "Chưa đánh giá"
                  })`}</Typography>
                </Stack>
                <Controller
                  name="review"
                  control={control}
                  rules={{
                    validate: {
                      checkMinLength: (review) => review.trim().length > 10,
                    },
                  }}
                  defaultValue=""
                  render={({ field: { onChange, onBlur, value } }) => (
                    <CustomInput
                      onChange={(e) => {
                        ratingValue === 0 &&
                          errors.review &&
                          setCheckRequireRating(true);
                        onChange(e);
                      }}
                      onBlur={onBlur}
                      value={value || ""}
                      multiline
                      minRows={2}
                      required
                      label="Nhận xét của bạn:"
                      helperText={
                        <>
                          Nhận xét phải có tối thiểu 10 ký tự. <br />
                          Một nhận xét có tâm của bạn có thể giúp cho nhiều
                          người chọn được sách hay đấy!
                        </>
                      }
                      sx={{ width: "100%" }}
                    />
                  )}
                />
              </Stack>
              {!errors.review && checkRequireRating && (
                <span style={{ color: "red", marginTop: "8px" }}>
                  Đánh giá sao cho sách nữa nha bạn!
                </span>
              )}
              {errors.review && errors.review.type === "checkMinLength" && (
                <span
                  style={{
                    color: "red",
                    marginTop: "8px",
                  }}
                >
                  Nhận xét dài dài thêm chút bạn ơi!
                </span>
              )}
              <CustomButton
                type="submit"
                variant="contained"
                disabled={appStatus === "loading"}
                sx={{ marginTop: theme.spacing(6), width: "100%" }}
              >
                {appStatus === "loading" ? (
                  <>
                    <CircularProgress
                      color="inherit"
                      sx={{
                        height: `${theme.spacing(6)} !important`,
                        width: `${theme.spacing(6)} !important`,
                      }}
                    />
                    <Typography variant="subtitle2" ml={3}>
                      Đang đăng đánh giá...
                    </Typography>
                  </>
                ) : (
                  "Đăng đánh giá"
                )}
              </CustomButton>
            </form>
          )}
        </Grid>
        <Grid item xs={12} sm={12} md={7}>
          <Stack spacing={3}>
            <Typography variant="h6" mb={1}>
              Tất cả đánh giá & nhận xét:
            </Typography>
            {!props.book.reviews.length ? (
              <Stack>
                <Typography>
                  Chưa ai đánh giá sách này. Bạn hãy là người đầu tiên!
                </Typography>
              </Stack>
            ) : (
              props.book.reviews
                .slice(skipReview, skipReview + DOCS_PER_PAGE)
                .map((review) => {
                  return (
                    <Stack key={review._id} spacing={2}>
                      <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="space-between"
                      >
                        <Stack
                          direction={{ xs: "column", sm: "column", md: "row" }}
                        >
                          <Rating
                            value={review.rating || 0}
                            precision={0.5}
                            readOnly
                          />
                          <Typography variant="caption" mt={1}>{`(${
                            reviewLabels[review.rating]
                          })`}</Typography>
                        </Stack>
                        <Typography>{`${convertISODateStringToDate(
                          review.createdAt
                        )}`}</Typography>
                      </Stack>
                      <Typography style={{ whiteSpace: "pre-line" }}>
                        {" "}
                        {/* style={{ whiteSpace: 'pre-line' }} to keep content's structure */}
                        {review.content}
                      </Typography>
                      <Typography variant="caption">
                        Từ: {getUsernameFromEmail(review.reviewer.email)}
                      </Typography>
                      <Divider light />
                    </Stack>
                  );
                })
            )}
          </Stack>
          {props.book.reviews.length ? (
            <CustomPagination
              count={totalReviews}
              page={currentReviewPage}
              onChange={handleChangePagination}
            />
          ) : null}
        </Grid>
      </Grid>
    )
  );
};

export default Reviews;
